import React from "react";
import { Link } from "react-router-dom";

import Card from "../ui/Card";

const CTA_LINK_CLASS =
  "inline-flex items-center justify-center gap-2 rounded tracking-wide uppercase text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main focus-visible:ring-theme-accent px-4 py-2.5 text-sm bg-theme-accent text-[#07090C] border border-theme-accent font-mono hover:opacity-90";

const BULLETS = [
  {
    icon: "radar",
    title: "Full inbox scan",
    body: "We pull recent inbound mail, flag real cold outreach, and skip newsletters and auto-replies.",
  },
  {
    icon: "target",
    title: "ICP-level fit scores",
    body: "Every pitch is checked against your brand profile so you reply to the deals that actually match.",
  },
  {
    icon: "edit_square",
    title: "Reply drafts ready to send",
    body: "Flipmail drafts on-brand responses you can tweak and drop into Gmail in one click.",
  },
];

/**
 * Reusable CTA for pages that need mailbox data (leads queue, history, analytics).
 */
export function InboxAuditPromoEmptyState({ primaryHref = "/audit/new/", className = "" }) {
  return (
    <Card
      className={`relative overflow-hidden border border-theme-border bg-theme-panel/40 p-0 ${className}`.trim()}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-theme-accent/[0.14] via-transparent to-theme-accent/[0.06]"
        aria-hidden
      />
      <div className="relative p-8 md:p-10 space-y-8">
        <div className="space-y-4 text-center md:text-left">
          <div className="inline-flex items-center gap-2 rounded-full border border-theme-border bg-theme-main/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-theme-dim">
            <span className="material-symbols-outlined text-[14px] text-theme-accent" aria-hidden>
              schedule
            </span>
            Typical first run: about 4 minutes
          </div>
          <h2 className="font-display font-bold italic text-2xl md:text-3xl text-theme-on-background leading-snug">
            Turn your inbox into a ranked lead queue
          </h2>
          <p className="text-theme-muted text-sm md:text-base max-w-2xl mx-auto md:mx-0 leading-relaxed">
            Run a one-time inbox audit to see who is pitching you, how well they fit your ICP, and where to focus
            first. No spam lists, no guesswork, just the conversations worth answering.
          </p>
        </div>

        <ul className="grid gap-4 sm:grid-cols-1 md:grid-cols-3">
          {BULLETS.map((item) => (
            <li
              key={item.title}
              className="rounded-xl border border-theme-border bg-theme-main/60 p-4 text-left space-y-2"
            >
              <span className="material-symbols-outlined text-[22px] text-theme-accent" aria-hidden>
                {item.icon}
              </span>
              <h3 className="font-display font-bold text-sm text-theme-on-background">{item.title}</h3>
              <p className="text-theme-muted text-xs leading-relaxed">{item.body}</p>
            </li>
          ))}
        </ul>

        <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3 pt-2">
          <Link to={primaryHref} className={`${CTA_LINK_CLASS} w-full sm:w-auto justify-center`}>
            Start inbox audit
          </Link>
          <p className="font-mono text-[11px] text-theme-dim text-center sm:text-left">
            Connect a mailbox in Settings if you have not already.
          </p>
        </div>
      </div>
    </Card>
  );
}
